import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Check if we're running in extension context
const isExtension = Boolean(
  window.chrome && chrome.runtime && chrome.runtime.id
);

// Improved browser detection
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isChrome = /chrome/.test(userAgent) && !/edge/.test(userAgent) && !/edg/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  
  return {
    isChrome,
    isAndroid,
    isSafari,
    isIOS,
    isSupportedBrowser: (isChrome && isAndroid) || (isSafari && isIOS)
  };
};

export const usePWA = () => {
  const [supportsPWA, setSupportsPWA] = useState(false);
  const [promptInstall, setPromptInstall] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const browserInfo = getBrowserInfo();

  // Check if app is running in standalone mode (installed)
  const checkIfInstalled = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(isStandalone);
    return isStandalone;
  };

  useEffect(() => {
    // Initial check for installed state
    checkIfInstalled();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
    };

    mediaQuery.addListener(handleDisplayModeChange);

    // Handle installation prompt
    const handleInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      if (!checkIfInstalled()) {
        setSupportsPWA(true);
        setPromptInstall(e);
      }
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt as any);

    // Handle successful installation
    window.addEventListener('appinstalled', () => {
      setSupportsPWA(false);
      setPromptInstall(null);
      setIsInstalled(true);
    });

    return () => {
      mediaQuery.removeListener(handleDisplayModeChange);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt as any);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!promptInstall) return;

    try {
      await promptInstall.prompt();
      const { outcome } = await promptInstall.userChoice;
      if (outcome === 'accepted') {
        setSupportsPWA(false);
        setPromptInstall(null);
        setIsInstalled(true);
      }
    } catch (error) {
      console.error('Error installing PWA:', error);
    }
  };

  return {
    supportsPWA: supportsPWA && browserInfo.isSupportedBrowser && !isInstalled,
    handleInstallClick,
    isPWAInstalled: isInstalled,
    browserInfo
  };
}; 